/**
 * Immersive mode — a kiosk-like presentation state.
 *
 * "Immersive" means two things happen together:
 *   1. The browser enters fullscreen (via Mantine's `useFullscreen`).
 *   2. The app shell (sidebar / header / banners) is hidden, so only the
 *      page's own content is visible.
 *
 * The two are coordinated here: entering/exiting via this context flips both,
 * and pressing Esc (which only the browser knows about) also drops the
 * sidebar-hidden flag so the user never ends up in a half-state.
 *
 * Architecture:
 *   - Pages that *control* the mode use `useImmersiveMode()` (write API).
 *   - Layouts/UI that *react* to the mode use `useIsImmersive()` (read API).
 *   - The provider owns the only call to Mantine's `useFullscreen`, so the
 *     browser API is wrapped once and everyone reads the same state.
 *
 * Add the provider once, at the app root (see `Router.tsx`).
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useFullscreen } from '@mantine/hooks';

interface ImmersiveModeContextValue {
  /** True while the page is in immersive mode (chrome hidden + browser fullscreen). */
  isActive: boolean;
  /** Enter immersive mode. Idempotent: safe to call if already active. */
  enter: () => void;
  /** Exit immersive mode. Idempotent: safe to call if already inactive. */
  exit: () => void;
  /** Flip the current state. */
  toggle: () => void;
}

const ImmersiveModeContext = createContext<ImmersiveModeContextValue | undefined>(undefined);

interface ImmersiveModeProviderProps {
  children: ReactNode;
}

/**
 * Mount once at the app root, above any layout or page that will read or
 * mutate immersive state.
 *
 * Owns the only call to Mantine's `useFullscreen` so the browser fullscreen
 * subscription is shared across consumers. Also reacts to `fullscreenchange`
 * (e.g. Esc) and clears the immersive flag so chrome reappears automatically.
 *
 * @example
 *   <ImmersiveModeProvider>
 *     <Routes ... />
 *   </ImmersiveModeProvider>
 */
export const ImmersiveModeProvider = ({ children }: ImmersiveModeProviderProps): JSX.Element => {
  const [isActive, setIsActive] = useState(false);
  const { toggle: toggleBrowserFullscreen, fullscreen: isBrowserFullscreen } = useFullscreen();

  // Refs let the returned callbacks be stable (empty/minimal deps) without
  // closing over stale browser state. Stability matters: consumers pass `exit`
  // into unmount-cleanup effects, and a changing identity would re-fire them.
  const isActiveRef = useRef(isActive);
  const isBrowserFullscreenRef = useRef(isBrowserFullscreen);
  const toggleBrowserFullscreenRef = useRef(toggleBrowserFullscreen);
  isActiveRef.current = isActive;
  isBrowserFullscreenRef.current = isBrowserFullscreen;
  toggleBrowserFullscreenRef.current = toggleBrowserFullscreen;

  // Esc / OS gesture exits browser fullscreen; mirror that into immersive
  // so chrome doesn't stay hidden after the user has effectively "left".
  useEffect(() => {
    if (!isBrowserFullscreen) setIsActive(false);
  }, [isBrowserFullscreen]);

  const enter = useCallback((): void => {
    setIsActive(true);
    if (!isBrowserFullscreenRef.current) toggleBrowserFullscreenRef.current();
  }, []);

  const exit = useCallback((): void => {
    setIsActive(false);
    if (isBrowserFullscreenRef.current) toggleBrowserFullscreenRef.current();
  }, []);

  const toggle = useCallback((): void => {
    if (isActiveRef.current) exit();
    else enter();
  }, [enter, exit]);

  return (
    <ImmersiveModeContext.Provider value={{ isActive, enter, exit, toggle }}>
      {children}
    </ImmersiveModeContext.Provider>
  );
};

/**
 * Page-facing API for driving immersive mode.
 *
 * Use this in any page that wants a "presentation" / kiosk-style view — for
 * example, a workout whiteboard projected on a gym TV. The page is responsible
 * for calling `exit()` (or relying on the unmount-cleanup pattern below) so a
 * stale immersive flag doesn't outlive the page.
 *
 * Returned callbacks have stable identities, so they're safe to put in effect
 * dependency arrays.
 *
 * Browser quirks worth knowing:
 *   - `enter()` triggers the browser Fullscreen API, which requires a user
 *     gesture. Call it from a click/keydown handler, not on mount.
 *   - Safari can reject the fullscreen request (e.g. when the document is
 *     embedded). The promise rejection is swallowed by Mantine's hook; from
 *     the page's perspective, immersive may "enter" the chrome-hidden state
 *     without the browser going fullscreen. Use `isActive` as the source of
 *     truth for your UI.
 *   - Pressing Esc exits the mode automatically — no extra handling needed
 *     in the consuming page.
 *
 * @example Toggle on a button click, with unmount cleanup:
 *   const { isActive, toggle, exit } = useImmersiveMode();
 *   useEffect(() => () => exit(), [exit]);
 *   return <Button onClick={toggle}>{isActive ? 'Exit' : 'Fullscreen'}</Button>;
 *
 * @throws if called outside an `ImmersiveModeProvider`.
 */
export const useImmersiveMode = (): ImmersiveModeContextValue => {
  const ctx = useContext(ImmersiveModeContext);
  if (!ctx) throw new Error('useImmersiveMode must be used within an ImmersiveModeProvider');
  return ctx;
};

/**
 * Read-only access to immersive state. Use this in layout / chrome components
 * that need to react to the flag (e.g. hide the sidebar) but should never
 * change it.
 *
 * Prefer this over `useImmersiveMode().isActive` in read-only consumers — the
 * narrower API prevents accidental writes and documents intent.
 *
 * @example
 *   const isImmersive = useIsImmersive();
 *   return <AppShell navbar={isImmersive ? undefined : <Navbar />} ... />;
 *
 * @throws if called outside an `ImmersiveModeProvider`.
 */
export const useIsImmersive = (): boolean => {
  const ctx = useContext(ImmersiveModeContext);
  if (!ctx) throw new Error('useIsImmersive must be used within an ImmersiveModeProvider');
  return ctx.isActive;
};
